import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { debounce } from '@/lib/debounce'
import { Building2, ChevronDown, ChevronUp, ChevronRight, TrendingUp, Sparkles, FileText, Megaphone } from 'lucide-react'
import { Company } from '@/types/company'
import { companyService } from '@/services/companyService'
import { userService } from '@/services/userService'
import { EditCompanyModal } from '@/components/company/EditCompanyModal'
import { AddCompanyModal } from '@/components/company/AddCompanyModal'
import { CompanyTable } from '@/components/company/CompanyTable'
import { CompanyFilters } from '@/components/company/CompanyFilters'
import { CompanyStats } from '@/components/company/CompanyStats'
import { useToast } from '@/components/ui/use-toast'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
// import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from '@/lib/utils'
import axios from '@/lib/axios'
import { utils as XLSXUtils, write as XLSXWrite } from 'xlsx'
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import 'jspdf-autotable'
import { useWebSocket } from '../../context/WebSocketContext';

interface MonthlyData {
  year: number;
  month: number;
  label: string;
  count: number;
  aiCount: number;
  manualCount: number;
}

interface CompanyStat {
  companyId: string;
  companyName: string;
  city: string;
  packageName: string;
  packageType: string;
  remainingCredits: number;
  totalCredits: number;
  packageExpiry: string | null;
  totalAnnouncements: number;
  approvedCount: number;
  pendingCount: number;
  rejectedCount: number;
  aiGeneratedCount: number;
  last30DaysCount: number;
  lastAnnouncementDate: string;
  firstAnnouncementDate: string;
  monthlyDistribution: MonthlyData[];
}

const CompanyManagementPage = () => {
  const { toast } = useToast()
  const [showAddCompanyModal, setShowAddCompanyModal] = React.useState(false)
  const [companies, setCompanies] = React.useState<Company[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const websocket = useWebSocket();
  const [searchQuery, setSearchQuery] = React.useState('')
  const [filterType, setFilterType] = React.useState<'all' | 'active' | 'inactive'>('all')
  const [licenseType, setLicenseType] = React.useState<'all' | 'premium' | 'trial'>('all')
  const [expiryStatus, setExpiryStatus] = React.useState<'all' | 'expired' | 'valid'>('all')
  const [expiringSoon, setExpiringSoon] = React.useState(false)
  const [selectedCompanies, setSelectedCompanies] = React.useState<Company[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [selectedCompany, setSelectedCompany] = React.useState<Company | null>(null);

  // Firma bazlı anons istatistikleri
  const [showAnnouncementStats, setShowAnnouncementStats] = React.useState(false);
  const [expandedCompanyId, setExpandedCompanyId] = React.useState<string | null>(null);
  const { data: companyStatsData, isLoading: isCompanyStatsLoading } = useQuery({
    queryKey: ["announcement-company-statistics"],
    queryFn: async () => {
      const response = await axios.get('/announcements/statistics/by-company');
      return response.data;
    },
    enabled: showAnnouncementStats
  });

  // Filtrelenmiş firma listesi
  const filteredCompanies = React.useMemo(() => {
    return companies.filter(company => {
      // Arama sorgusuna göre filtreleme
      const matchesSearch = 
        company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        company.phone.toLowerCase().includes(searchQuery.toLowerCase())

      // Lisans aktif/pasif durumuna göre filtreleme
      const matchesFilter = 
        filterType === 'all' ||
        (filterType === 'active' && company.license.isActive) ||
        (filterType === 'inactive' && !company.license.isActive)

      // Lisans tipine göre filtreleme (Premium/Trial)
      const matchesLicenseType =
        licenseType === 'all' ||
        (licenseType === 'premium' && company.license.type === 'premium') ||
        (licenseType === 'trial' && company.license.type === 'trial')

      // Lisans süresi durumuna göre filtreleme
      const currentDate = new Date()
      const expiryDate = new Date(company.license.expiryDate)
      const isExpired = expiryDate < currentDate
      
      const matchesExpiryStatus =
        expiryStatus === 'all' ||
        (expiryStatus === 'expired' && isExpired) ||
        (expiryStatus === 'valid' && !isExpired)

      // 30 gün içinde bitecek lisanslara göre filtreleme
      const diffTime = expiryDate.getTime() - currentDate.getTime()
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      const isExpiringSoon = diffDays <= 30 && diffDays > 0 && company.license.isActive
      
      const matchesExpiringSoon = !expiringSoon || (expiringSoon && isExpiringSoon)

      return matchesSearch && matchesFilter && matchesLicenseType && matchesExpiryStatus && matchesExpiringSoon
    })
  }, [companies, searchQuery, filterType, licenseType, expiryStatus, expiringSoon])

  // Debounced arama
  const debouncedSearch = React.useCallback(
    debounce((query: string) => {
      setSearchQuery(query)
    }, 300),
    []
  )

  // Firmaları getir
  const fetchCompanies = React.useCallback(async () => {
    try {
      const response = await companyService.getAllCompanies()
      setCompanies(Array.isArray(response) ? response : [])
    } catch (error) {
      console.error('Firmalar getirilirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Firmalar getirilirken bir hata oluştu.'
      })
      setCompanies([])
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  // Firma ekle
  const handleAddCompany = async (data: any) => {
    try {
      await companyService.createCompany(data)
      await fetchCompanies()
      toast({
        title: 'Başarılı',
        description: 'Firma başarıyla eklendi.'
      })
    } catch (error) {
      console.error('Firma eklenirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Firma eklenirken bir hata oluştu.'
      })
      throw error
    }
  }

  // Firma güncelle
  const handleUpdateCompany = async (id: string, data: any) => {
    try {
      await companyService.updateCompany(id, data)
      await fetchCompanies()
      toast({
        title: 'Başarılı',
        description: 'Firma başarıyla güncellendi.'
      })
    } catch (error) {
      console.error('Firma güncellenirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Firma güncellenirken bir hata oluştu.'
      })
      throw error
    }
  }

  // Firma sil
  const handleDeleteCompany = async (id: string) => {
    try {
      await companyService.deleteCompany(id)
      await fetchCompanies()
      toast({
        title: 'Başarılı',
        description: 'Firma başarıyla silindi.'
      })
    } catch (error) {
      console.error('Firma silinirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Firma silinirken bir hata oluştu.'
      })
    }
  }

  // Lisans güncelle
  const handleUpdateLicense = async (id: string, data: any) => {
    try {
      await companyService.updateLicense(id, data)
      await fetchCompanies()
      toast({
        title: 'Başarılı',
        description: 'Lisans başarıyla güncellendi.'
      })
    } catch (error) {
      console.error('Lisans güncellenirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Lisans güncellenirken bir hata oluştu.'
      })
      throw error
    }
  }

  // Manager olarak devam et
  const handleSwitchToManager = async (managerId: string) => {
    try {
      console.log('Yönetici olarak devam et işlemi başlatılıyor:', { managerId });
      
      // 1. Super admin token'ını al
      const superAdminToken = localStorage.getItem('token');
      if (!superAdminToken) {
        throw new Error('Super admin token bulunamadı');
      }
      
      // 2. API'den token al
      const response = await companyService.getLoginToken(managerId);
      const token = response.token || response.data?.token;
      
      if (!token) {
        throw new Error('Token alınamadı');
      }
      
      // 3. Manager panel URL'ini oluştur
      const baseUrl = import.meta.env.VITE_MANAGER_URL || 'https://panel.cloudmedia.com.tr';
      const loginUrl = `${baseUrl}/auth/direct-login?token=${encodeURIComponent(token)}`;

      // 4. Yeni pencerede aç
      window.open(loginUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Giriş yapılırken hata:', error);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Giriş yapılırken bir hata oluştu.'
      });
    }
  }

  // Excel'e aktarma
  const handleExportExcel = async (companies: Company[]) => {
    try {
      const data = companies.map((company) => ({
        "Firma Adı": company.name,
        "Şehir": company.city,
        "Sektör": company.sector,
        "Telefon": company.phone,
        "Lisans Durumu": company.license.isActive ? "Aktif" : "Pasif",
        "Lisans Bitiş": new Date(company.license.expiryDate).toLocaleDateString("tr-TR"),
        "Yönetici": company.admins?.[0]?.name || "-",
        "E-posta": company.admins?.[0]?.email || "-",
      }));

      const ws = XLSXUtils.json_to_sheet(data);
      const wb = XLSXUtils.book_new();
      XLSXUtils.book_append_sheet(wb, ws, "Firmalar");
      
      // Excel dosyasını indir
      const excelBuffer = XLSXWrite(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `firmalar_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Başarılı",
        description: `${companies.length} firma Excel'e aktarıldı.`,
      });
    } catch (error) {
      console.error("Excel'e aktarılırken hata:", error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Excel'e aktarılırken bir hata oluştu.",
      });
    }
  };

  // PDF'e aktarma
  const handleExportPdf = async (companies: Company[]) => {
    try {      
      // A4 yatay PDF dökümanı oluştur (297mm x 210mm)
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      // Kenar boşlukları
      const margin = {
        top: 20,
        right: 10,
        bottom: 20,
        left: 10
      };

      // Kullanılabilir sayfa genişliği
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      // const contentWidth = pageWidth - margin.left - margin.right;

      // Başlık alanı
      doc.setFillColor(248, 249, 250);
      doc.rect(0, 0, pageWidth, 35, "F");

      // Ana başlık
      doc.setFontSize(20);
      doc.setTextColor(0, 0, 0);
      doc.text("Firma Listesi", margin.left, margin.top);

      // Alt başlık (sol)
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      const date = new Date().toLocaleDateString("tr-TR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
      doc.text(`Oluşturulma: ${date}`, margin.left, margin.top + 10);

      // Firma sayısı (sağ)
      doc.text(
        `Toplam: ${companies.length} firma`,
        pageWidth - margin.right,
        margin.top + 10,
        { align: "right" }
      );

      // Tablo sütun genişlikleri (toplam 277mm)
      const cols = {
        firma: 80,    // Firma adı için geniş alan
        sehir: 35,    // Şehir için yeterli
        sektor: 35,   // Sektör için yeterli
        lisans: 25,   // Durum için dar
        bitis: 30,    // Tarih için orta
        yonetici: 35, // İsim için orta
        email: 37     // E-posta için kalan alan
      };

      // Tablo verilerini hazırla
      const tableData = companies.map((company) => [
        company.name,
        company.city,
        company.sector,
        company.license.isActive ? "Aktif" : "Pasif",
        new Date(company.license.expiryDate).toLocaleDateString("tr-TR"),
        company.admins?.[0]?.name || "-",
        company.admins?.[0]?.email || "-",
      ]);

      // Tabloyu oluştur
      autoTable(doc, {
        head: [["Firma Adı", "Şehir", "Sektör", "Lisans", "Bitiş Tarihi", "Yönetici", "E-posta"]],
        body: tableData,
        startY: 40,
        margin: margin,
        theme: 'plain',
        headStyles: {
          fillColor: [241, 245, 249],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          cellPadding: 4,
          fontSize: 10,
          minCellHeight: 10,
          valign: 'middle',
        },
        bodyStyles: {
          textColor: [50, 50, 50],
          cellPadding: 4,
          fontSize: 9,
          minCellHeight: 8,
          valign: 'middle',
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        columnStyles: {
          0: { cellWidth: cols.firma },
          1: { cellWidth: cols.sehir },
          2: { cellWidth: cols.sektor },
          3: { cellWidth: cols.lisans },
          4: { cellWidth: cols.bitis },
          5: { cellWidth: cols.yonetici },
          6: { cellWidth: cols.email },
        },
        didDrawPage: (_data) => {
          // Sayfa numarası
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          const currentPage = doc.getNumberOfPages();
          const totalPages = doc.getNumberOfPages();
          doc.text(
            `Sayfa ${currentPage}/${totalPages}`,
            pageWidth - margin.right,
            pageHeight - 10,
            { align: "right" }
          );
        },
      });

      // PDF'i indir
      doc.save(`firmalar_${new Date().toISOString().split("T")[0]}.pdf`);

      toast({
        title: "Başarılı",
        description: `${companies.length} firma PDF'e aktarıldı.`,
      });
    } catch (error) {
      console.error("PDF'e aktarılırken hata:", error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "PDF'e aktarılırken bir hata oluştu.",
      });
    }
  };

  const toggleCompany = (company: Company) => {
    setSelectedCompanies((prev) =>
      prev.find((c) => c._id === company._id)
        ? prev.filter((c) => c._id !== company._id)
        : [...prev, company]
    );
  };

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedCompanies([...companies]);
    } else {
      setSelectedCompanies([]);
    }
  };

  // Seçimleri temizleme fonksiyonu
  const clearSelection = () => {
    setSelectedCompanies([]);
  };

  // Bir firmanın seçili olup olmadığını kontrol et
  const isSelected = (company: Company) =>
    selectedCompanies.some((c) => c._id === company._id);

  const isAllSelected = companies.length > 0 && selectedCompanies.length === companies.length;

  // WebSocket mesajlarını dinle
  React.useEffect(() => {
    if (!websocket) return;

    const handleMessage = (event: MessageEvent) => {
      const message = JSON.parse(event.data);
      console.log('📨 WebSocket mesajı alındı:', message);

      if (message.type === 'ADMIN_STATUS_CHANGE') {
        const { adminId, isOnline } = message.data;
        console.log('👤 Admin durumu güncellendi:', { adminId, isOnline });
        
        setCompanies(prevCompanies => 
          prevCompanies.map(company => ({
            ...company,
            admins: company.admins.map(admin => 
              admin._id === adminId 
                ? { ...admin, isOnline }
                : admin
            )
          }))
        );
      }
    };

    websocket.onmessage = handleMessage;

    return () => {
      console.log('🔌 WebSocket mesaj dinleyicisi kaldırıldı');
    };
  }, [websocket]);

  React.useEffect(() => {
    fetchCompanies()
  }, [fetchCompanies])

  if (isLoading) {
    return (
      <div className="h-full flex-1">
        <div className="flex-1 space-y-6 p-8 pt-6">
          {/* Hero Section Skeleton */}
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <div className="h-56 bg-gradient-to-r from-blue-800 to-blue-500 p-8">
              <div className="flex justify-between items-start relative w-full h-full">
                <div className="space-y-3 flex-1">
                  <Skeleton className="h-4 w-40 bg-white/20" />
                  <Skeleton className="h-9 w-80 bg-white/30" />
                  <Skeleton className="h-16 w-[500px] bg-white/20" />
                </div>
                <Skeleton className="w-40 h-40 rounded-lg bg-white/10" />
              </div>
            </div>
          </div>

          {/* CompanyStats skeleton - Birinci satır kartları */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
                <CardContent className="p-0">
                  <div className="relative">
                    {/* Arka plan dekoratif gradient */}
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-slate-200 blur-2xl"></div>

                    <div className="p-6 relative">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <Skeleton className="h-4 w-[120px] mb-2" />
                          <Skeleton className="h-9 w-[70px]" />
                        </div>
                        <Skeleton className="h-14 w-14 rounded-xl" />
                      </div>

                      {/* Alt bilgi çubuğu */}
                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Skeleton className="w-2 h-2 rounded-full mr-2" />
                            <Skeleton className="h-4 w-[120px]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* CompanyStats skeleton - İkinci satır kartları */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
                <CardContent className="p-0">
                  <div className="relative">
                    {/* Arka plan dekoratif gradient */}
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-slate-200 blur-2xl"></div>

                    <div className="p-6 relative">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <Skeleton className="h-4 w-[120px] mb-2" />
                          <Skeleton className="h-9 w-[70px]" />
                        </div>
                        <Skeleton className="h-14 w-14 rounded-xl" />
                      </div>

                      {/* Alt bilgi çubuğu */}
                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Skeleton className="w-2 h-2 rounded-full mr-2" />
                            <Skeleton className="h-4 w-[120px]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* CompanyFilters skeleton - Gerçek boyutlarda filtreler */}
          <Card className="p-6">
            <div className="flex flex-col md:flex-row gap-4 justify-between">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-[120px]" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full md:w-1/2">
                <div className="space-y-2">
                  <Skeleton className="h-5 w-[100px]" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-5 w-[100px]" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-5 w-[100px]" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
            </div>
          </Card>

          {/* Seçim araçları skeleton - Gerçek boyutlarda */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-6" />
              <Skeleton className="h-6 w-[140px]" />
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-10 w-[130px]" />
              <Skeleton className="h-10 w-[130px]" />
              <Skeleton className="h-10 w-[130px]" />
            </div>
          </div>

          {/* CompanyTable skeleton - Gerçek tablo yapısında */}
          <Card>
            <div className="overflow-hidden border border-slate-200 rounded-md">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="py-3.5 px-4">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-5 w-5 rounded" />
                        <Skeleton className="h-5 w-[150px]" />
                      </div>
                    </TableHead>
                    <TableHead className="py-3.5 px-4">
                      <Skeleton className="h-5 w-[120px]" />
                    </TableHead>
                    <TableHead className="py-3.5 px-4">
                      <Skeleton className="h-5 w-[100px]" />
                    </TableHead>
                    <TableHead className="py-3.5 px-4">
                      <Skeleton className="h-5 w-[100px]" />
                    </TableHead>
                    <TableHead className="py-3.5 px-4">
                      <Skeleton className="h-5 w-[100px]" />
                    </TableHead>
                    <TableHead className="py-3.5 px-4">
                      <Skeleton className="h-5 w-[80px]" />
                    </TableHead>
                    <TableHead className="py-3.5 px-4 text-right">
                      <Skeleton className="h-5 w-[80px] ml-auto" />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i} className="hover:bg-slate-50">
                      <TableCell className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-5 w-5 rounded" />
                          <div className="space-y-1">
                            <Skeleton className="h-5 w-[180px]" />
                            <div className="flex items-center">
                              <Skeleton className="h-4 w-4 rounded mr-1" />
                              <Skeleton className="h-4 w-[100px]" />
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-4">
                        <div className="space-y-1">
                          <Skeleton className="h-5 w-[140px]" />
                          <div className="flex items-center">
                            <Skeleton className="h-4 w-4 rounded mr-1" />
                            <Skeleton className="h-4 w-[90px]" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-4">
                        <Skeleton className="h-7 w-[120px] rounded-full" />
                      </TableCell>
                      <TableCell className="py-4 px-4">
                        <div className="space-y-1">
                          <div className="flex items-center">
                            <Skeleton className="h-4 w-4 rounded mr-1" />
                            <Skeleton className="h-5 w-[80px]" />
                          </div>
                          <div className="flex items-center">
                            <Skeleton className="h-4 w-4 rounded mr-1" />
                            <Skeleton className="h-5 w-[70px]" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-4">
                        <div className="space-y-1">
                          <div className="flex items-center">
                            <Skeleton className="h-4 w-4 rounded mr-1" />
                            <Skeleton className="h-5 w-[100px]" />
                          </div>
                          <div className="flex items-center">
                            <Skeleton className="h-4 w-4 rounded mr-1" />
                            <Skeleton className="h-5 w-[80px]" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-4">
                        <div className="flex items-center">
                          <Skeleton className="h-4 w-4 rounded mr-1" />
                          <Skeleton className="h-5 w-[70px]" />
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-4 text-right">
                        <div className="flex justify-end">
                          <Skeleton className="h-8 w-8 rounded" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* Pagination skeleton - Gerçek boyutlarda */}
          <div className="flex items-center justify-between py-4">
            <Skeleton className="h-6 w-[200px]" />
            <div className="flex items-center gap-1">
              <Skeleton className="h-10 w-[90px]" />
              <div className="flex gap-1">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className="h-10 w-10 rounded" />
                ))}
              </div>
              <Skeleton className="h-10 w-[90px]" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
          <div
            className="flex"
            style={{
              background: 'linear-gradient(to right, #1e40af, #3b82f6)',
            }}
          >
            <div className="p-8 flex justify-between items-start relative w-full">
              <div className="space-y-3 flex-1">
                <div className="text-white/80 text-sm font-medium">
                  Firma Yönetim Paneli
                </div>
                <h1 className="text-3xl font-bold text-white">
                  Firma ve Lisans Kontrolü
                </h1>
                <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                  Tüm firmaları görüntüleyin, yeni firma ekleyin, lisans bilgilerini
                  düzenleyin ve toplu işlemler yapın. Firma yöneticilerine kolayca
                  e-posta gönderin veya rapor alın.
                </p>
              </div>
              <div
                className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }}
              >
                <Building2 className="w-20 h-20 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {/* İstatistikler */}
        <CompanyStats companies={companies} />

        {/* Firma Bazlı Anons İstatistikleri */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <button
            onClick={() => setShowAnnouncementStats(prev => !prev)}
            className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-indigo-500/10">
                <Megaphone className="w-5 h-5 text-indigo-500" strokeWidth={2.5} />
              </div>
              <div className="text-left">
                <h3 className="text-sm font-bold text-slate-900">Firma Bazlı Anons İstatistikleri</h3>
                <p className="text-xs text-slate-500 mt-0.5">Hangi firma ne kadar anons oluşturmuş detaylı görüntüle</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {companyStatsData && (
                <Badge variant="secondary" className="text-xs">
                  {companyStatsData.totalCompanies} firma · {companyStatsData.totalAnnouncements} anons
                </Badge>
              )}
              {showAnnouncementStats ? (
                <ChevronUp className="h-5 w-5 text-slate-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-400" />
              )}
            </div>
          </button>

          {showAnnouncementStats && (
            <div className="border-t border-slate-200">
              {isCompanyStatsLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-300 border-t-indigo-500"></div>
                </div>
              ) : companyStatsData?.companies?.length > 0 ? (
                <>
                  {/* Aylık Dağılım Grafiği */}
                  {companyStatsData.monthlyDistribution?.length > 0 && (() => {
                    const maxCount = Math.max(...companyStatsData.monthlyDistribution.map((m: any) => m.count));
                    return (
                      <div className="px-6 py-5 border-b border-slate-200">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="text-sm font-bold text-slate-900">Aylık Anons Dağılımı</h4>
                            <p className="text-xs text-slate-400 mt-0.5">Son 12 aylık anons oluşturma trendi</p>
                          </div>
                          {companyStatsData.peakMonth && (
                            <Badge variant="secondary" className="text-xs">
                              En yoğun: {companyStatsData.peakMonth.label} ({companyStatsData.peakMonth.count})
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-end gap-1.5 h-32">
                          {companyStatsData.monthlyDistribution.map((month: any, i: number) => {
                            const heightPercent = maxCount > 0 ? (month.count / maxCount) * 100 : 0;
                            const aiHeightPercent = maxCount > 0 ? (month.aiCount / maxCount) * 100 : 0;
                            const manualHeightPercent = heightPercent - aiHeightPercent;
                            return (
                              <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                                {/* Tooltip */}
                                <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                                  {month.label}: {month.count} ({month.aiCount} AI)
                                </div>
                                {/* Bar */}
                                <div className="w-full flex flex-col justify-end h-24">
                                  <div
                                    className="w-full bg-purple-400 rounded-t-sm transition-all"
                                    style={{ height: `${aiHeightPercent}%`, minHeight: month.aiCount > 0 ? '2px' : '0' }}
                                  />
                                  <div
                                    className="w-full bg-blue-400 rounded-b-sm transition-all"
                                    style={{ height: `${manualHeightPercent}%`, minHeight: month.manualCount > 0 ? '2px' : '0' }}
                                  />
                                </div>
                                {/* Sayı */}
                                <span className="text-[10px] font-medium text-slate-600">{month.count}</span>
                                {/* Ay etiketi */}
                                <span className="text-[9px] text-slate-400">{month.label.split(' ')[0]}</span>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-4 mt-3 justify-end">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-sm bg-purple-400" />
                            <span className="text-[10px] text-slate-500">AI Anons</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-sm bg-blue-400" />
                            <span className="text-[10px] text-slate-500">Manuel Anons</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-50 hover:to-slate-100">
                        <TableHead className="py-3 text-xs font-bold text-slate-600">Firma</TableHead>
                        <TableHead className="py-3 text-xs font-bold text-slate-600">Paket</TableHead>
                        <TableHead className="py-3 text-xs font-bold text-slate-600 text-center">Toplam</TableHead>
                        <TableHead className="py-3 text-xs font-bold text-slate-600 text-center">Son 30 Gün</TableHead>
                        <TableHead className="py-3 text-xs font-bold text-slate-600 text-center">AI</TableHead>
                        <TableHead className="py-3 text-xs font-bold text-slate-600 text-center">Onaylı</TableHead>
                        <TableHead className="py-3 text-xs font-bold text-slate-600 text-center">Bekleyen</TableHead>
                        <TableHead className="py-3 text-xs font-bold text-slate-600 text-center">Kredi</TableHead>
                        <TableHead className="py-3 text-xs font-bold text-slate-600 text-right pr-6">Son Anons</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {companyStatsData.companies.map((company: CompanyStat) => {
                        const isExpanded = expandedCompanyId === company.companyId;
                        const manualCount = company.totalAnnouncements - company.aiGeneratedCount;
                        const aiPercent = company.totalAnnouncements > 0 ? Math.round((company.aiGeneratedCount / company.totalAnnouncements) * 100) : 0;
                        const approvedPercent = company.totalAnnouncements > 0 ? Math.round((company.approvedCount / company.totalAnnouncements) * 100) : 0;
                        const creditPercent = company.totalCredits > 0 ? Math.round((company.remainingCredits / company.totalCredits) * 100) : 0;

                        return (
                          <React.Fragment key={company.companyId}>
                            <TableRow
                              onClick={() => setExpandedCompanyId(isExpanded ? null : company.companyId)}
                              className={cn(
                                "border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer transition-colors",
                                isExpanded && "bg-indigo-50/50"
                              )}
                            >
                              <TableCell className="py-3">
                                <div className="flex items-center gap-2">
                                  <ChevronRight className={cn("h-3.5 w-3.5 text-slate-400 transition-transform", isExpanded && "rotate-90")} />
                                  <div>
                                    <div className="font-medium text-sm text-slate-800">{company.companyName}</div>
                                    <div className="text-xs text-slate-400">{company.city}</div>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="py-3">
                                <Badge variant="outline" className="text-xs font-medium">
                                  {company.packageName !== '-' ? company.packageName : company.packageType}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-3 text-center">
                                <span className="font-bold text-sm text-slate-900">{company.totalAnnouncements}</span>
                              </TableCell>
                              <TableCell className="py-3 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <TrendingUp className="h-3.5 w-3.5 text-green-500" />
                                  <span className="text-sm font-medium text-slate-700">{company.last30DaysCount}</span>
                                </div>
                              </TableCell>
                              <TableCell className="py-3 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                                  <span className="text-sm text-slate-700">{company.aiGeneratedCount}</span>
                                </div>
                              </TableCell>
                              <TableCell className="py-3 text-center">
                                <span className="text-sm text-green-600 font-medium">{company.approvedCount}</span>
                              </TableCell>
                              <TableCell className="py-3 text-center">
                                <span className={cn("text-sm font-medium", company.pendingCount > 0 ? "text-amber-600" : "text-slate-400")}>
                                  {company.pendingCount}
                                </span>
                              </TableCell>
                              <TableCell className="py-3 text-center">
                                <div className="text-xs">
                                  <span className={cn(
                                    "font-bold",
                                    company.remainingCredits <= 0 ? "text-red-500" : company.remainingCredits <= 3 ? "text-amber-500" : "text-slate-700"
                                  )}>
                                    {company.remainingCredits}
                                  </span>
                                  <span className="text-slate-400">/{company.totalCredits}</span>
                                </div>
                              </TableCell>
                              <TableCell className="py-3 text-right pr-6">
                                <span className="text-xs text-slate-500">
                                  {company.lastAnnouncementDate
                                    ? new Date(company.lastAnnouncementDate).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' })
                                    : '-'}
                                </span>
                              </TableCell>
                            </TableRow>

                            {/* Detay Kartı */}
                            {isExpanded && (
                              <TableRow className="bg-slate-50/80">
                                <TableCell colSpan={9} className="p-0">
                                  <div className="px-6 py-5 space-y-4">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <h4 className="text-base font-bold text-slate-900">{company.companyName}</h4>
                                        <p className="text-xs text-slate-500">{company.city} — İlk Anons: {company.firstAnnouncementDate ? new Date(company.firstAnnouncementDate).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }) : '-'}</p>
                                      </div>
                                      {company.packageExpiry && (
                                        <div className="text-xs text-slate-500">
                                          Paket Bitiş: <span className={cn("font-medium", new Date(company.packageExpiry) < new Date() ? "text-red-500" : "text-slate-700")}>
                                            {new Date(company.packageExpiry).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' })}
                                          </span>
                                        </div>
                                      )}
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                      {/* AI vs Manuel */}
                                      <div className="bg-white rounded-lg border border-slate-200 p-3.5">
                                        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Anons Tipi</div>
                                        <div className="flex items-end gap-3">
                                          <div>
                                            <div className="flex items-center gap-1.5 mb-1">
                                              <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                                              <span className="text-lg font-bold text-slate-900">{company.aiGeneratedCount}</span>
                                              <span className="text-[10px] text-slate-400">AI</span>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                              <FileText className="h-3.5 w-3.5 text-blue-500" />
                                              <span className="text-lg font-bold text-slate-900">{manualCount}</span>
                                              <span className="text-[10px] text-slate-400">Manuel</span>
                                            </div>
                                          </div>
                                          <div className="flex-1">
                                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                              <div className="h-full bg-purple-500 rounded-full" style={{ width: `${aiPercent}%` }} />
                                            </div>
                                            <div className="text-[10px] text-slate-400 mt-0.5 text-right">%{aiPercent} AI</div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Durum Dağılımı */}
                                      <div className="bg-white rounded-lg border border-slate-200 p-3.5">
                                        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Durum Dağılımı</div>
                                        <div className="space-y-1.5">
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-green-600">Onaylı</span>
                                            <span className="text-sm font-bold text-slate-900">{company.approvedCount}</span>
                                          </div>
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-amber-600">Bekleyen</span>
                                            <span className="text-sm font-bold text-slate-900">{company.pendingCount}</span>
                                          </div>
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-red-500">Reddedilen</span>
                                            <span className="text-sm font-bold text-slate-900">{company.rejectedCount}</span>
                                          </div>
                                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
                                            <div className="h-full bg-green-500" style={{ width: `${approvedPercent}%` }} />
                                            <div className="h-full bg-amber-400" style={{ width: `${company.totalAnnouncements > 0 ? Math.round((company.pendingCount / company.totalAnnouncements) * 100) : 0}%` }} />
                                            <div className="h-full bg-red-400" style={{ width: `${company.totalAnnouncements > 0 ? Math.round((company.rejectedCount / company.totalAnnouncements) * 100) : 0}%` }} />
                                          </div>
                                        </div>
                                      </div>

                                      {/* Kredi Durumu */}
                                      <div className="bg-white rounded-lg border border-slate-200 p-3.5">
                                        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Kredi Durumu</div>
                                        <div className="flex items-center gap-3">
                                          <div className="relative w-14 h-14">
                                            <svg className="w-14 h-14 -rotate-90" viewBox="0 0 36 36">
                                              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                                              <circle cx="18" cy="18" r="15.9" fill="none"
                                                stroke={creditPercent <= 10 ? '#ef4444' : creditPercent <= 30 ? '#f59e0b' : '#22c55e'}
                                                strokeWidth="3" strokeDasharray={`${creditPercent} ${100 - creditPercent}`} strokeLinecap="round" />
                                            </svg>
                                            <div className="absolute inset-0 flex items-center justify-center">
                                              <span className="text-xs font-bold text-slate-700">%{creditPercent}</span>
                                            </div>
                                          </div>
                                          <div>
                                            <div className="text-xl font-bold text-slate-900">{company.remainingCredits}</div>
                                            <div className="text-[10px] text-slate-400">/ {company.totalCredits} kredi kaldı</div>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Aktivite */}
                                      <div className="bg-white rounded-lg border border-slate-200 p-3.5">
                                        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Aktivite</div>
                                        <div className="space-y-2">
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-slate-500">Son 30 gün</span>
                                            <div className="flex items-center gap-1">
                                              <TrendingUp className="h-3.5 w-3.5 text-green-500" />
                                              <span className="text-sm font-bold text-slate-900">{company.last30DaysCount}</span>
                                            </div>
                                          </div>
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-slate-500">İlk anons</span>
                                            <span className="text-xs font-medium text-slate-700">{company.firstAnnouncementDate ? new Date(company.firstAnnouncementDate).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</span>
                                          </div>
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-slate-500">Son anons</span>
                                            <span className="text-xs font-medium text-slate-700">{company.lastAnnouncementDate ? new Date(company.lastAnnouncementDate).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</span>
                                          </div>
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-slate-500">Paket</span>
                                            <Badge variant="outline" className="text-[10px] h-5">{company.packageName !== '-' ? company.packageName : company.packageType}</Badge>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Firma Bazlı Aylık Dağılım */}
                                    {company.monthlyDistribution?.length > 0 && (() => {
                                      const maxMonthCount = Math.max(...company.monthlyDistribution.map((m: MonthlyData) => m.count));
                                      const peakCompanyMonth = company.monthlyDistribution.reduce((max: MonthlyData, m: MonthlyData) => m.count > max.count ? m : max, company.monthlyDistribution[0]);
                                      return (
                                        <div className="bg-white rounded-lg border border-slate-200 p-3.5">
                                          <div className="flex items-center justify-between mb-3">
                                            <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Aylık Anons Dağılımı</div>
                                            <Badge variant="secondary" className="text-[10px] h-5">
                                              En yoğun: {peakCompanyMonth.label} ({peakCompanyMonth.count})
                                            </Badge>
                                          </div>
                                          <div className="flex items-end gap-1 h-20">
                                            {company.monthlyDistribution.map((month: MonthlyData, i: number) => {
                                              const heightPct = maxMonthCount > 0 ? (month.count / maxMonthCount) * 100 : 0;
                                              const aiPct = maxMonthCount > 0 ? (month.aiCount / maxMonthCount) * 100 : 0;
                                              const manualPct = heightPct - aiPct;
                                              return (
                                                <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                                                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                                                    {month.label}: {month.count} ({month.aiCount} AI)
                                                  </div>
                                                  <div className="w-full flex flex-col justify-end h-14">
                                                    <div className="w-full bg-purple-400 rounded-t-sm" style={{ height: `${aiPct}%`, minHeight: month.aiCount > 0 ? '2px' : '0' }} />
                                                    <div className="w-full bg-blue-400 rounded-b-sm" style={{ height: `${manualPct}%`, minHeight: month.manualCount > 0 ? '2px' : '0' }} />
                                                  </div>
                                                  <span className="text-[8px] font-medium text-slate-500">{month.count}</span>
                                                  <span className="text-[7px] text-slate-400">{month.label.split(' ')[0]}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                          <div className="flex items-center gap-3 mt-2 justify-end">
                                            <div className="flex items-center gap-1">
                                              <div className="w-2 h-2 rounded-sm bg-purple-400" />
                                              <span className="text-[9px] text-slate-400">AI</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                              <div className="w-2 h-2 rounded-sm bg-blue-400" />
                                              <span className="text-[9px] text-slate-400">Manuel</span>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <Megaphone className="h-10 w-10 mb-2 text-slate-300" />
                  <p className="text-sm">Henüz firma bazlı anons istatistiği bulunmuyor</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filtreler */}
        <CompanyFilters
          onSearchChange={debouncedSearch}
          onFilterChange={setFilterType}
          onLicenseTypeChange={setLicenseType}
          onExpiryStatusChange={setExpiryStatus}
          onExpiringSoonChange={setExpiringSoon}
          filterValue={filterType}
          licenseTypeValue={licenseType}
          expiryStatusValue={expiryStatus}
          expiringSoonValue={expiringSoon}
          onAddCompany={() => setShowAddCompanyModal(true)}
        />

        {/* Firma Tablosu */}
        {filteredCompanies.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 border rounded-lg bg-background">
            <p className="text-lg font-medium mb-2">Firma Bulunamadı</p>
            <p className="text-muted-foreground text-center max-w-md">
              {searchQuery
                ? 'Arama kriterlerinize uygun firma bulunamadı. Lütfen farklı bir arama yapmayı deneyin.'
                : 'Henüz hiç firma eklenmemiş. Yeni bir firma eklemek için "Firma Ekle" butonunu kullanın.'}
            </p>
          </div>
        ) : (
          <CompanyTable
            companies={filteredCompanies}
            onEdit={(company) => {
              console.log('Düzenlenmek üzere seçilen firma:', company);
              setSelectedCompany({...company}); // Derin kopya oluştur
              setIsEditModalOpen(true);
            }}
            onDelete={handleDeleteCompany}
            onUpdateLicense={handleUpdateLicense}
            onExportExcel={handleExportExcel}
            onExportPdf={handleExportPdf}
            onAddAdmin={async (data) => {
              try {
                await userService.createUser({
                  name: data.name,
                  email: data.email,
                  password: data.password,
                  company: data.companyId,
                  role: 'manager'
                })
                await fetchCompanies()
                toast({
                  title: 'Başarılı',
                  description: 'Yönetici başarıyla eklendi.'
                })
              } catch (error) {
                console.error('Yönetici eklenirken hata:', error)
                toast({
                  variant: 'destructive',
                  title: 'Hata',
                  description: 'Yönetici eklenirken bir hata oluştu.'
                })
              }
            }}
            onSwitchToManager={handleSwitchToManager}
            selectedCompanies={selectedCompanies}
            onToggleCompany={toggleCompany}
            onToggleAll={toggleAll}
            isSelected={isSelected}
            isAllSelected={isAllSelected}
            clearSelection={clearSelection}
          />
        )}

        {/* Modaller */}
        {showAddCompanyModal && (
          <AddCompanyModal
            onClose={() => setShowAddCompanyModal(false)}
            onSubmit={handleAddCompany}
          />
        )}

        {isEditModalOpen && selectedCompany && (
          <EditCompanyModal
            key={selectedCompany._id}
            company={selectedCompany}
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedCompany(null);
            }}
            onSubmit={handleUpdateCompany}
          />
        )}
      </div>
    </div>
  )
}

export default CompanyManagementPage
